import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ArrowRight, ChevronDown, Menu, Phone, X } from "lucide-react";
import { SERVICES, SERVICE_CATEGORIES, PHONE_DISPLAY, waLink } from "../data/content";
import { SERVICE_ICONS } from "./serviceIcons";
import { AnimatePresence, motion } from "framer-motion";

const links = [
  { to: "/", label: "Home", id: "home" },
  { to: "/about", label: "About Us", id: "about" },
  { to: "/industries", label: "Industries", id: "industries" },
  { to: "/work", label: "Our Work", id: "work" },
  { to: "/blog", label: "Blog", id: "blog" },
  { to: "/contact", label: "Contact Us", id: "contact" },
];

export const Logo = ({ dark = false }) => (
  <Link to="/" data-testid="navbar-logo-link" className="flex items-center shrink-0" aria-label="GrowMyRestro — Home">
    <img src="/growmyrestro-logo.png" alt="GrowMyRestro — Hospitality Growth Partner" className={dark ? "h-28 w-28 rounded-xl object-contain" : "h-16 w-16 sm:h-[68px] sm:w-[68px] rounded-lg object-contain"} />
  </Link>
);

const MegaMenu = ({ open, onClose }) => (
  <div
    className={`fixed left-0 right-0 top-[76px] z-40 transition-all duration-300 ${
      open ? "opacity-100 visible translate-y-0 pointer-events-auto" : "opacity-0 invisible translate-y-3 pointer-events-none"
    }`}
  >
    <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-14 max-h-[calc(100vh-96px)] overflow-y-auto pb-4">
      <div className="bg-white rounded-[2rem] border border-line shadow-[0_40px_90px_rgba(45,18,77,0.18)] p-8" data-testid="services-mega-menu">
        <div className="grid grid-cols-3 gap-10">
          {SERVICE_CATEGORIES.map((cat) => (
            <div key={cat.id}>
              <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-terracotta font-semibold" data-testid={`mega-category-${cat.id}`}>{cat.title}</p>
              <p className="mt-1 text-xs text-ink-muted">{cat.tagline}</p>
              <ul className="mt-4 space-y-3.5 border-t border-line pt-4">
                {SERVICES.filter((s) => s.category === cat.id).map((s) => {
                  const Icon = SERVICE_ICONS[s.icon];
                  return (
                    <li key={s.id}>
                      <Link to={s.slug} data-testid={`mega-service-${s.id}`} onClick={onClose} className="group/item flex items-center gap-3">
                        <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition-transform duration-300 group-hover/item:scale-110" style={{ background: s.color }}>
                          <Icon className="w-4 h-4 text-cream" />
                        </span>
                        <span>
                          <span className="block font-display font-bold text-sm text-aubergine leading-tight group-hover/item:text-terracotta transition-colors">{s.title}</span>
                          <span className="block text-[11px] text-ink-muted mt-0.5">{s.tagline}</span>
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-6 pt-5 border-t border-line flex items-center justify-between">
          <p className="text-sm text-ink-muted">Every growth need for your restaurant — <strong className="text-aubergine">under one roof</strong>.</p>
          <Link to="/services" data-testid="mega-view-all-services" onClick={onClose} className="inline-flex items-center gap-2 text-sm font-bold text-terracotta hover:text-saffron transition-colors">
            View all services <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  </div>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setMegaOpen(false);
  }, [location.pathname]);

  const navCls = (isActive) =>
    `px-3 py-2 text-sm font-semibold rounded-full transition-colors ${isActive ? "text-royal bg-royal-soft" : "text-ink hover:text-aubergine"}`;

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-cream/90 backdrop-blur-xl border-b border-line shadow-[0_8px_30px_rgba(45,18,77,0.06)]" : "bg-cream/60 backdrop-blur-md"
      }`}
    >
      <nav className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-14 h-[76px] flex items-center justify-between gap-4">
        <Logo />
        <div className="hidden lg:flex items-center gap-1">
          <NavLink to="/" data-testid="nav-link-home" className={({ isActive }) => navCls(isActive)}>Home</NavLink>
          <NavLink to="/about" data-testid="nav-link-about" className={({ isActive }) => navCls(isActive)}>About Us</NavLink>
          <div
            className="relative group h-[76px] flex items-center"
            onMouseEnter={() => setMegaOpen(true)}
            onMouseLeave={() => setMegaOpen(false)}
            onFocus={() => setMegaOpen(true)}
            onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget)) setMegaOpen(false);
            }}
          >
            <NavLink to="/services" data-testid="nav-link-services" onClick={() => setMegaOpen(false)} className={({ isActive }) => `${navCls(isActive)} inline-flex items-center gap-1`}>
              Services <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${megaOpen ? "rotate-180" : ""}`} />
            </NavLink>
            <MegaMenu open={megaOpen} onClose={() => setMegaOpen(false)} />
          </div>
          <NavLink to="/industries" data-testid="nav-link-industries" className={({ isActive }) => navCls(isActive)}>Industries</NavLink>
          <NavLink to="/work" data-testid="nav-link-work" className={({ isActive }) => navCls(isActive)}>Our Work</NavLink>
          <NavLink to="/blog" data-testid="nav-link-blog" className={({ isActive }) => navCls(isActive)}>Blog</NavLink>
          <NavLink to="/contact" data-testid="nav-link-contact" className={({ isActive }) => navCls(isActive)}>Contact Us</NavLink>
        </div>
        <div className="flex items-center gap-3">
          <a
            href={`tel:${PHONE_DISPLAY.replace(/\s/g, "")}`}
            data-testid="nav-phone-link"
            className="hidden xl:inline-flex items-center gap-2 text-sm font-bold text-aubergine hover:text-saffron transition-colors"
          >
            <Phone className="w-4 h-4" /> {PHONE_DISPLAY}
          </a>
          <Link
            to="/contact"
            data-testid="nav-lets-talk-button"
            className="hidden sm:inline-flex items-center gap-2 bg-aubergine text-cream text-sm font-bold px-5 py-2.5 rounded-full hover:bg-saffron hover:text-aubergine-deep transition-colors duration-300"
          >
            Let's Talk
          </Link>
          <button
            data-testid="nav-mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="lg:hidden w-10 h-10 rounded-full border border-line bg-white/70 flex items-center justify-center text-aubergine"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </nav>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35 }}
            className="lg:hidden overflow-hidden bg-cream/95 backdrop-blur-xl border-b border-line"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {[...links.slice(0, 2), { to: "/services", label: "Services" }, ...links.slice(2)].map((l) => (
                <Link key={l.to} to={l.to} data-testid={`mobile-nav-${l.id || "services"}`} className="py-2.5 text-base font-bold text-aubergine border-b border-line/60">
                  {l.label}
                </Link>
              ))}
              <a href={waLink()} data-testid="mobile-nav-whatsapp" className="mt-4 inline-flex justify-center bg-cardamom text-cream font-bold px-5 py-3 rounded-full">
                Chat on WhatsApp
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
